import React from 'react';

type ParsedAddressField = {
  key: string;
  label: string;
  value: string;
};

type AddressDisplayProps = {
  data: string;
};

const formatLabel = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
};

const shouldDisplayField = (key: string): boolean => {
  // Hide ID fields to prevent internal identifiers from being displayed
  return !key.toLowerCase().endsWith('id');
};

const parseAddressData = (
  text: string | null | undefined
): ParsedAddressField[] => {
  if (!text) {
    return [];
  }

  try {
    const parsed = JSON.parse(text);

    return Object.entries(parsed)
      .filter(([key]) => shouldDisplayField(key))
      .filter(
        ([, value]) => value !== null && value !== undefined && value !== ''
      )
      .map(([key, value]) => ({
        key,
        label: formatLabel(key),
        value: String(value)
      }));
  } catch (error) {
    console.error('Failed to parse address data:', error);
    return [];
  }
};

export const AddressDisplay: React.FC<AddressDisplayProps> = ({ data }) => {
  const fieldEntries = React.useMemo<ParsedAddressField[]>(() => {
    return parseAddressData(data);
  }, [data]);

  if (fieldEntries.length === 0) {
    return null;
  }

  return (
    <div className="address-display" data-testid="address-display">
      <div className="address-display__content">
        {fieldEntries.map(({ key, label, value }) => (
          <div
            key={key}
            className="address-display__row"
            data-testid="address-display-row"
          >
            <span
              className="address-display__label"
              data-testid="address-display-label"
            >
              {label}:
            </span>
            <span
              className="address-display__value"
              data-testid="address-display-value"
            >
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
